/**
 * Презентационные хелперы экрана «Тарифы» — чистые функции, без React.
 */

import dayjs from 'dayjs';

import {type ClinicPackage, type ClinicQuote, clinicQuoteFor} from '@services/clients';
import {formatNumber} from '@utils/format';

/** Пояснение калькулятора: в какой пакет попадает штат (или ступень «Сеть»). */
export const explainQuote = (
  seats: number,
  packages: readonly ClinicPackage[],
  overPrice: number,
): string => {
  const quote: ClinicQuote = clinicQuoteFor(seats, packages, overPrice);
  if (quote.over > 0) {
    const last = packages[packages.length - 1];
    return `Пакет «${last.name}» (${formatNumber(last.price)}) + ${quote.over} × ${formatNumber(overPrice)} за места сверх ${last.upTo}`;
  }
  return `Попадает в пакет «${quote.name}» (${quote.range})`;
};

/** Черновик пакета в режиме правки (строковые поля инпутов + id строки). */
export type PackageDraft = {id: string; name: string; upTo: number; price: number};

export type Validation = {ok: boolean; message: string};

/**
 * Строгая валидация пакетов: границы `upTo` и цена строго возрастают —
 * иначе при росте штата сумма могла бы упасть (защита биллинга §5.2).
 */
export const validatePackages = (packages: readonly PackageDraft[]): Validation => {
  for (let i = 1; i < packages.length; i += 1) {
    if (packages[i].upTo <= packages[i - 1].upTo) {
      return {ok: false, message: 'Границы пакетов должны возрастать (до 3 → до 6 → …)'};
    }
    if (packages[i].price < packages[i - 1].price) {
      return {
        ok: false,
        message: 'Цена пакета не может быть ниже предыдущего — иначе сумма упадёт при росте',
      };
    }
  }
  return {ok: true, message: 'Пакеты растут корректно · цена не падает при росте штата'};
};

let draftSeq = 0;
export const toDrafts = (packages: readonly ClinicPackage[]): PackageDraft[] =>
  packages.map((p) => ({id: `pkg-${(draftSeq += 1)}`, ...p}));

export const newDraft = (afterLast: PackageDraft): PackageDraft => ({
  id: `pkg-${(draftSeq += 1)}`,
  name: 'Новый пакет',
  upTo: afterLast.upTo + 4,
  price: afterLast.price + 3000,
});

/** «12 июн 2026» без точки после месяца. */
export const fmtTariffDate = (iso: string): string =>
  dayjs(iso).format('D MMM YYYY').replace('.', '');

/** Строка версии в шапке карточки; v0 = линейка ещё не заведена на BE. */
export const tariffVersionLine = (version: number, updatedAt: string): string =>
  version === 0
    ? 'тариф ещё не задан — сохраните первую версию'
    : `v${version} · обновлено ${fmtTariffDate(updatedAt)}`;
